import { useState } from 'react';
import { useCompany } from '@/company/context';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/content';
import { Segmented, StatusPill } from '@/components/ui/kit';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import appsNavCopy from '@/i18n/ns/apps-nav';
import appsLoadingCopy from '@/i18n/ns/apps-loading';
import artifactPanelsCopy from '@/i18n/ns/artifact-panels';
import { useTimeLabel } from '../artifact-panels/format';
import { useArtifactLibrary } from '../artifact-panels/use-artifact-library';
import { takeAppsSearch } from './apps-search-handoff';
import type { LibraryItemVM, LibrarySegment } from '../artifact-panels/types';
import type { LibraryOpenTarget } from '../artifacts/view-models';
import { EmptyState, LoadingState, StartInChatButton } from '../primitives';
import { AppCardPreview } from './app-card-preview';
import { rememberAppOpening, useAppOpeningPrincipal } from './app-opening';
import { prefetchBootstrap } from './runtime/runtime-control';
import { useSessionBoundFetch } from '@/lib/use-session-guard';
import './apps-page.css';

// The Apps place: the Artifact Library's own authorized list (the same hook, the same data and segments, no new
// store) filtered to the App kind and drawn as cards. A card opens the genuine saved App by its original
// identity. It reads nothing until the account and company are known.

export interface AppsLibraryProps {
  /** Returns false when the host cannot open this exact version. */
  onOpen: (target: LibraryOpenTarget) => boolean | void;
  /** The empty state's next step, as on Skills: the new-App chat. */
  onMake?: () => void;
  /** The viewer's own display name: an App owned by someone else shows "By {owner}". */
  viewerName?: string | null;
}

/** The first letter of the name, for the App page's own icon. */
export const appInitial = (title: string) => Array.from(title.trim())[0]?.toUpperCase() ?? '·';

function AppCard({
  item,
  showOwner,
  onOpen,
}: {
  item: LibraryItemVM;
  showOwner: boolean;
  onOpen: (id: string) => void;
}) {
  const copy = useNamespaceCopy(artifactPanelsCopy);
  const nav = useNamespaceCopy(appsNavCopy);
  const time = useTimeLabel();
  const updated = time(item.updatedAt);
  return (
    <li className="ap-app-card-item">
      <button type="button" className="ap-app-card" onClick={() => onOpen(item.id)}>
        <AppCardPreview title={item.title} src={item.thumbnailUrl} />
        <span className="ap-app-card-name">{item.title}</span>
        {updated && (
          <span className="ap-app-card-meta">
            {formatCopy(copy.libraryUpdated, { time: updated })}
          </span>
        )}
        {showOwner && (
          <span className="ap-app-card-meta">
            {formatCopy(copy.libraryOwner, { owner: item.ownerLabel })}
          </span>
        )}
        {item.audienceLabel && <StatusPill>{item.audienceLabel}</StatusPill>}
      </button>
    </li>
  );
}

export function AppsLibrary({ onOpen, onMake, viewerName }: AppsLibraryProps) {
  // The Library's "Apps match" link opens this list already searching for the same words.
  const library = useArtifactLibrary('only', useState(takeAppsSearch)[0]);
  const session = useSessionBoundFetch();
  const owner = useCompany().data;
  const principal = useAppOpeningPrincipal();
  const copy = useNamespaceCopy(artifactPanelsCopy);
  const nav = useNamespaceCopy(appsNavCopy);
  const loading = useNamespaceCopy(appsLoadingCopy);
  const { vm } = library;
  const filtered = vm.query.trim() !== '';
  const ready = vm.load === 'ready';
  return (
    <section className="ap-apps" aria-label={copy.libraryKindApps} data-panel="apps">
      {/* The Skills page's search field, then the Library's own segments. */}
      <input
        type="search"
        className="ws-resource-search"
        aria-label={nav.appsSearch}
        placeholder={nav.appsSearch}
        value={vm.query}
        onChange={(event) => library.setQuery(event.currentTarget.value)}
      />
      <div className="ap-toolbar">
        <Segmented<LibrarySegment>
          label={copy.libraryKindApps}
          value={vm.segment}
          onChange={library.setSegment}
          options={[
            { value: 'all', label: copy.librarySegmentAll },
            { value: 'mine', label: copy.librarySegmentMine },
            { value: 'shared', label: copy.librarySegmentShared },
          ]}
        />
      </div>
      {!ready && vm.load !== 'error' && <LoadingState label={loading.loading} />}
      {vm.load === 'error' && (
        <Callout
          tone="info"
          actions={
            <Button type="button" variant="outline" size="sm" onClick={library.retry}>
              {copy.retry}
            </Button>
          }
        >
          {copy.libraryErrorBody}
        </Callout>
      )}
      {ready && vm.items.length === 0 && (
        <EmptyState
          icon="app"
          title={filtered ? copy.libraryNoMatchTitle : nav.appsEmptyTitle}
          description={filtered ? copy.libraryNoMatchBody : nav.appsEmptyBody}
        >
          {!filtered && onMake && (
            <StartInChatButton next label={nav.appsCreateFirst} onClick={onMake} />
          )}
        </EmptyState>
      )}
      {ready && vm.items.length > 0 && (
        <ul className="ap-app-grid">
          {vm.items.map((item) => (
            <AppCard
              key={item.id}
              item={item}
              showOwner={
                vm.segment === 'shared' ||
                (Boolean(viewerName) && item.ownerLabel !== viewerName && item.ownerLabel !== '')
              }
              onOpen={(id) => {
                const target = library.openTarget(id);
                if (!target) return;
                // The App opens on its own picture and name, not a blank wait (app-opening.tsx).
                rememberAppOpening(principal, target.artifactId, {
                  name: item.title,
                  picture: item.thumbnailUrl,
                });
                if (onOpen(target) === false) return;
                // Its runtime ticket is asked for now, alongside the saved view, not after it.
                const companyId = owner?.company?.id;
                const actorId = owner?.membership?.userId;
                if (companyId && actorId)
                  prefetchBootstrap(session, {
                    companyId,
                    actorId,
                    artifactId: target.artifactId,
                    revisionId: target.revisionId,
                    versionId: target.versionId,
                    purpose: target.purpose,
                  });
              }}
            />
          ))}
        </ul>
      )}
      {ready && vm.hasMore && (
        <Button type="button" variant="outline" onClick={library.loadMore}>
          {copy.libraryMore}
        </Button>
      )}
    </section>
  );
}

/** The Apps library, mounted only for the committed account and company (as the Artifact Library gate does). */
export function AppsLibraryGate(props: AppsLibraryProps) {
  const company = useCompany().data;
  const resolved = Boolean(company?.company?.id && company.membership?.userId);
  const loading = useNamespaceCopy(appsLoadingCopy);
  if (!resolved) return <LoadingState label={loading.loading} />;
  return <AppsLibrary {...props} />;
}
